import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, DatePicker, Select, Input, message } from 'antd';
import { SearchOutlined, DownloadOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { AuditLog } from '../utils/permissions';

const { RangePicker } = DatePicker;
const { Option } = Select;

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterPage, setFilterPage] = useState('all');

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = () => {
    setLoading(true);
    try {
      const savedLogs = JSON.parse(localStorage.getItem('auditLogs') || '[]');
      setLogs(savedLogs.reverse()); // 最新的在前面
    } catch (error) {
      message.error('加载日志失败');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (logs.length === 0) {
      message.warning('没有日志可导出');
      return;
    }
    
    const csvContent = [
      '时间,用户,页面,操作,详情',
      ...logs.map(log => 
        `${new Date(log.timestamp).toLocaleString()},${log.username},${log.page},${log.action},"${log.details || ''}"`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('日志导出成功');
  };

  const getActionColor = (action: string) => {
    const colors: Record<string, string> = {
      view: 'blue',
      edit: 'orange',
      delete: 'red',
      create: 'green',
      export: 'purple',
      import: 'cyan',
      test: 'geekblue',
      deploy: 'magenta'
    };
    return colors[action] || 'default';
  };

  const getActionDisplayName = (action: string) => {
    const names: Record<string, string> = {
      view: '查看',
      edit: '编辑',
      delete: '删除',
      create: '创建',
      export: '导出',
      import: '导入',
      test: '测试',
      deploy: '部署'
    };
    return names[action] || action;
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp: number) => new Date(timestamp).toLocaleString(),
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '页面',
      dataIndex: 'page',
      key: 'page',
      width: 120,
      render: (page: string) => {
        const pageNames: Record<string, string> = {
          dashboard: '仪表盘',
          memory: '记忆管理',
          profile: '用户画像',
          script: '话术管理',
          agent: '智能体管理',
          knowledge: '知识库管理',
          workflow: '工作流管理',
          debug: '调试工具',
          model: '大模型管理',
          reports: '后台报表'
        };
        return pageNames[page] || page;
      }
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action: string) => (
        <Tag color={getActionColor(action)}>
          {getActionDisplayName(action)}
        </Tag>
      ),
    },
    {
      title: '详情',
      dataIndex: 'details',
      key: 'details',
      render: (details: any) => details ? JSON.stringify(details) : '-',
    },
  ];

  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchText || 
      log.username.toLowerCase().includes(searchText.toLowerCase()) ||
      log.page.toLowerCase().includes(searchText.toLowerCase()) ||
      log.action.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    const matchesPage = filterPage === 'all' || log.page === filterPage;
    
    return matchesSearch && matchesAction && matchesPage;
  });

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="操作审计日志"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadLogs}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={logs.length === 0}
            >
              导出日志
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="搜索用户、页面或操作"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              value={filterAction}
              onChange={setFilterAction}
              style={{ width: 120 }}
            >
              <Option value="all">全部操作</Option>
              <Option value="view">查看</Option>
              <Option value="edit">编辑</Option>
              <Option value="delete">删除</Option>
              <Option value="create">创建</Option>
              <Option value="export">导出</Option>
              <Option value="import">导入</Option>
              <Option value="test">测试</Option>
              <Option value="deploy">部署</Option>
            </Select>
            <Select
              value={filterPage}
              onChange={setFilterPage}
              style={{ width: 120 }}
            >
              <Option value="all">全部页面</Option>
              <Option value="dashboard">仪表盘</Option>
              <Option value="memory">记忆管理</Option>
              <Option value="profile">用户画像</Option>
              <Option value="script">话术管理</Option>
              <Option value="agent">智能体管理</Option>
              <Option value="knowledge">知识库管理</Option>
              <Option value="workflow">工作流管理</Option>
              <Option value="debug">调试工具</Option>
              <Option value="model">大模型管理</Option>
              <Option value="reports">后台报表</Option>
            </Select>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredLogs}
          rowKey="id"
          loading={loading}
          pagination={{
            total: filteredLogs.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条日志`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default AuditLogs;
